import type { ConflictResult, ConflictRule, ConflictRuleContext } from '../conflict.types';
import type { ScheduleMap } from '../../models/expanded-schedule.model';
import {
  doShiftsOverlap,
  isOvernightShift,
  doOvernightShiftOverlapWithMorning,
  type ShiftTimeEntry,
} from '../../utils/shift-overlap.utils';

type ShiftUnitMeta = { startTime: string; endTime: string; allowOverlap: boolean };

/**
 * Detects shift-overlap conflicts in the schedule.
 *
 * For each date and employee, checks all assigned shift unit pairs for:
 *   1. **Duplicate** — same shift unit assigned twice
 *   2. **Overlap** — two shift units with overlapping time windows (same day)
 *   3. **Overnight overlap** — overnight shift from previous day overlapping with a morning shift
 *
 * Respects `allowOverlap` flags: if either shift unit in a pair has `allowOverlap: true`,
 * the overlap is not treated as a conflict.
 *
 * Requires `context.shiftUnits` to be provided with shift unit time metadata.
 * Returns empty results if context is missing.
 */
export class ShiftOverlapConflictRule implements ConflictRule {
  readonly type = 'overlap' as const;

  detect(schedule: ScheduleMap, _events: unknown[], context?: ConflictRuleContext): ConflictResult[] {
    const shiftUnits = context?.shiftUnits;
    if (!shiftUnits || shiftUnits.size === 0) return [];

    const results: ConflictResult[] = [];
    const dates = Object.keys(schedule).sort((a, b) => a.localeCompare(b));

    for (const date of dates) {
      const daySchedule = schedule[date];
      if (!daySchedule) continue;

      for (const [personId, unitIds] of Object.entries(daySchedule)) {
        // Check same-day duplicates and overlaps
        this.checkSameDayConflicts(results, date, personId, unitIds, shiftUnits);

        // Check overnight overlap from previous day
        const prevDate = this.getPreviousDate(date, dates);
        if (prevDate) {
          const prevDaySchedule = schedule[prevDate];
          const prevUnitIds = prevDaySchedule?.[personId];
          if (prevUnitIds && prevUnitIds.length > 0) {
            this.checkOvernightConflicts(results, prevDate, date, personId, prevUnitIds, unitIds, shiftUnits);
          }
        }
      }
    }

    return results;
  }

  /**
   * Check for duplicate and same-day overlap conflicts between shift units
   * assigned to the same person on the same date.
   */
  private checkSameDayConflicts(
    results: ConflictResult[],
    date: string,
    personId: string,
    unitIds: string[],
    shiftUnits: Map<string, ShiftUnitMeta>
  ): void {
    for (let i = 0; i < unitIds.length; i++) {
      for (let j = i + 1; j < unitIds.length; j++) {
        const unitIdA = unitIds[i];
        const unitIdB = unitIds[j];

        // Duplicate: same shift unit assigned twice
        if (unitIdA === unitIdB) {
          results.push(this.buildResult('duplicate', date, personId, unitIdA, unitIdB, 'Duplicate shift'));
          continue;
        }

        const metaA = shiftUnits.get(unitIdA);
        const metaB = shiftUnits.get(unitIdB);
        if (!metaA || !metaB) continue;

        // Respect allowOverlap
        if (metaA.allowOverlap || metaB.allowOverlap) continue;

        const entryA: ShiftTimeEntry = { startTime: metaA.startTime, endTime: metaA.endTime };
        const entryB: ShiftTimeEntry = { startTime: metaB.startTime, endTime: metaB.endTime };

        if (doShiftsOverlap(entryA, entryB)) {
          results.push(this.buildResult('overlap', date, personId, unitIdA, unitIdB, 'Shift overlap'));
        }
      }
    }
  }

  /**
   * Check for overnight overlap conflicts: an overnight shift from the previous day
   * whose end time overlaps with a shift on the current day.
   */
  private checkOvernightConflicts(
    results: ConflictResult[],
    prevDate: string,
    currentDate: string,
    personId: string,
    prevUnitIds: string[],
    currentUnitIds: string[],
    shiftUnits: Map<string, ShiftUnitMeta>
  ): void {
    const overnightShifts = this.filterOvernightShifts(prevUnitIds, shiftUnits);

    for (const { unitId: prevUnitId, meta: prevMeta, entry: prevEntry } of overnightShifts) {
      for (const currentUnitId of currentUnitIds) {
        const currentMeta = shiftUnits.get(currentUnitId);
        if (!currentMeta) continue;
        if (prevMeta.allowOverlap || currentMeta.allowOverlap) continue;

        const currentEntry: ShiftTimeEntry = { startTime: currentMeta.startTime, endTime: currentMeta.endTime };
        if (!doOvernightShiftOverlapWithMorning(prevEntry, currentEntry)) continue;

        results.push(this.buildResult('overnight_overlap', currentDate, personId, currentUnitId, prevUnitId, 'Overnight shift overlap'));
        results.push(this.buildResult('overnight_overlap', prevDate, personId, prevUnitId, currentUnitId, 'Overnight shift overlap'));
      }
    }
  }

  /** Filter shift unit IDs to only those that are overnight shifts with valid metadata. */
  private filterOvernightShifts(
    unitIds: string[],
    shiftUnits: Map<string, ShiftUnitMeta>
  ): Array<{ unitId: string; meta: ShiftUnitMeta; entry: ShiftTimeEntry }> {
    const result: Array<{ unitId: string; meta: ShiftUnitMeta; entry: ShiftTimeEntry }> = [];
    for (const unitId of unitIds) {
      const meta = shiftUnits.get(unitId);
      if (!meta) continue;
      const entry: ShiftTimeEntry = { startTime: meta.startTime, endTime: meta.endTime };
      if (isOvernightShift(entry)) {
        result.push({ unitId, meta, entry });
      }
    }
    return result;
  }

  private buildResult(
    type: 'duplicate' | 'overlap' | 'overnight_overlap',
    date: string,
    personId: string,
    shiftUnitId: string,
    conflictingShiftUnitId: string,
    eventLabel: string
  ): ConflictResult {
    return {
      type,
      date,
      personId,
      eventLabel,
      calendarId: '',
      shiftUnitId,
      conflictingShiftUnitId,
    };
  }

  /**
   * Find the previous date in the sorted dates array.
   * Returns undefined if the previous calendar day is not in the schedule.
   */
  private getPreviousDate(date: string, sortedDates: string[]): string | undefined {
    const d = new Date(date + 'T00:00:00Z');
    d.setUTCDate(d.getUTCDate() - 1);
    const prevStr = d.toISOString().split('T')[0];
    return sortedDates.includes(prevStr) ? prevStr : undefined;
  }
}
